import axios from 'axios';
import { getJobApiCall, postJobApiCall, patchJobApiCall, deleteJobApiCall } from '../../utils/jobRequest';
import { handle403Logout } from '../../utils/request';
import {
  SET_CHAT_CONVERSATION,
  SET_CHAT_MESSAGES,
  UPSERT_CHAT_MESSAGE,
  SET_CHAT_PRESENCE,
  APPLY_CHAT_READ_RECEIPT,
  RESET_CHAT,
  SET_CHAT_INBOX,
  APPEND_CHAT_INBOX,
  UPSERT_INBOX_ITEM,
  PREPEND_CHAT_MESSAGES,
  APPEND_CHAT_MESSAGES,
  SET_TOTAL_UNREAD,
  SET_TEAM_INBOX,
  UPSERT_TEAM_INBOX_ITEM,
  SET_TEAM_CONVERSATION,
  SET_TEAM_MESSAGES,
  UPSERT_TEAM_MESSAGE,
  APPEND_TEAM_MESSAGES,
  APPLY_TEAM_READ_RECEIPT,
  SET_TEAM_TOTAL_UNREAD,
} from '../Types/types';

// ─── Plain action creators ─────────────────────────────────────────────────────

export const setChatConversation = (data) => ({ type: SET_CHAT_CONVERSATION, payload: data });
export const setChatMessages = (messages) => ({ type: SET_CHAT_MESSAGES, payload: messages });
export const upsertChatMessage = (message) => ({ type: UPSERT_CHAT_MESSAGE, payload: message });
export const setInboxItems = (items) => ({ type: SET_CHAT_INBOX, payload: items });
export const appendInboxItems = (items) => ({ type: APPEND_CHAT_INBOX, payload: items });
export const upsertInboxItem = (item) => ({ type: UPSERT_INBOX_ITEM, payload: item });
export const prependChatMessages = (messages) => ({ type: PREPEND_CHAT_MESSAGES, payload: messages });
export const appendChatMessages = (messages) => ({ type: APPEND_CHAT_MESSAGES, payload: messages });

export const setChatPresence = (recruiterOnline, candidateOnline) => ({
  type: SET_CHAT_PRESENCE,
  payload: { recruiter_online: recruiterOnline, candidate_online: candidateOnline },
});

export const applyReadReceipt = (senderType, lastReadMessageId) => ({
  type: APPLY_CHAT_READ_RECEIPT,
  payload: { sender_type: senderType, last_read_message_id: lastReadMessageId },
});

export const resetChat = () => ({ type: RESET_CHAT });
export const setTotalUnread = (count) => ({ type: SET_TOTAL_UNREAD, payload: count });

// ─── Candidate-side axios helper (x-api-key auth) ─────────────────────────────

function candidateChatCall(method, path, body) {
  return axios({
    method,
    url: `${process.env.REACT_APP_AI_API_KEY}${path}`,
    headers: {
      'x-api-key': process.env.REACT_APP_CANDIDATE_CHAT_API_KEY || '',
      'Content-Type': 'application/json',
    },
    data: body,
  });
}

// ─── Recruiter thunks (Bearer JWT via existing jobRequest utils) ───────────────

export const loadRecruiterConversation = (jcId) => async (dispatch) => {
  try {
    const [convRes, msgRes] = await Promise.all([
      getJobApiCall(`job-candidates/${jcId}/conversation`),
      getJobApiCall(`job-candidates/${jcId}/messages?limit=50&order_by=desc`),
    ]);
    const convData = convRes?.status === 200 ? convRes.data.data : null;
    if (convData) {
      dispatch(setChatConversation(convData));
    }
    const msgs = msgRes?.status === 200 ? (msgRes.data.data?.messages || []) : [];
    dispatch(setChatMessages(msgs));
    return { success: true, conversation: convData, messages: msgs };
  } catch (error) {
    handle403Logout(error);
    return { success: false, conversation: null };
  }
};

export const loadOlderRecruiterMessages = (jcId, cursorId) => async (dispatch) => {
  try {
    const res = await getJobApiCall(
      `job-candidates/${jcId}/messages?limit=50&order_by=desc&cursor=${cursorId}&direction=before`
    );
    if (res?.status === 200) {
      const msgs = res.data.data?.messages || [];
      if (msgs.length > 0) {
        dispatch(appendChatMessages(msgs));
        return { success: true, hasMore: true };
      }
      return { success: true, hasMore: false };
    }
    return { success: false, hasMore: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false, hasMore: false };
  }
};

export const refreshRecruiterConversation = (jcId) => async (dispatch, getState) => {
  try {
    const res = await getJobApiCall(`job-candidates/${jcId}/conversation`);
    if (res?.status !== 200) return;
    const fresh = res.data.data;
    // Drop late responses for a conversation the recruiter has already switched away from
    const current = getState().chat.conversation;
    if (current?.conversation_id && fresh?.conversation_id &&
        String(current.conversation_id) !== String(fresh.conversation_id)) return;
    dispatch(setChatConversation(fresh));
  } catch (_) {}
};

export const sendRecruiterMessage = (jcId, bodyText, channel) => async (dispatch) => {
  try {
    const res = await postJobApiCall(`job-candidates/${jcId}/messages`, {
      body_text: bodyText,
      channel,
    });
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertChatMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    // postJobApiCall returns the axios error object (not throws) for 400
    const errDetail = res?.response?.data?.detail;
    if (errDetail) return { success: false, errorDetail: errDetail };
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const sendRecruiterMessageWithAttachment = (jcId, file, caption) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('body_text', caption);
    const res = await axios.post(
      `${process.env.REACT_APP_AI_API_KEY}job-candidates/${jcId}/messages/with-attachment`,
      formData,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
      },
    );
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertChatMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const markRecruiterRead = (jcId, lastReadMessageId) => async () => {
  try {
    await patchJobApiCall(`job-candidates/${jcId}/read`, {
      last_read_message_id: lastReadMessageId,
    });
    return { success: true };
  } catch (_) {
    return { success: false };
  }
};

export const RECRUITER_INBOX_PAGE_SIZE = 50;

// Page 1 replaces the inbox. Later pages are only returned, so the caller can
// drop a stale page (e.g. search changed mid-request) before appending it with
// appendInboxItems.
export const loadRecruiterInbox = (search = '', page = 1) => async (dispatch) => {
  try {
    let path = `job-candidates/conversations?page=${page}&page_size=${RECRUITER_INBOX_PAGE_SIZE}`;
    if (search) path += `&search=${encodeURIComponent(search)}`;
    const res = await getJobApiCall(path);
    if (res?.status !== 200) return { success: false };
    const items = res.data.data?.items || [];
    const total = res.data.data?.total;
    if (page === 1) dispatch(setInboxItems(items));
    const hasMore = typeof total === 'number'
      ? page * RECRUITER_INBOX_PAGE_SIZE < total
      : items.length === RECRUITER_INBOX_PAGE_SIZE;
    return { success: true, items, hasMore, totalUnread: res.data.data?.total_unread || 0 };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const loadUnreadSummary = () => async (dispatch) => {
  try {
    const res = await getJobApiCall('job-candidates/conversations/unread-summary');
    if (res?.status === 200) {
      dispatch(setTotalUnread(res.data.data?.total_unread_global || 0));
    }
    return { success: true };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const getRecruiterChatLink = (jcId) => async () => {
  try {
    const res = await getJobApiCall(`job-candidates/${jcId}/chat-link`);
    if (res?.status === 200) return { success: true, data: res.data?.data };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

// ─── Candidate thunks (x-api-key) ─────────────────────────────────────────────

export const loadOlderCandidateMessages = (jcId, cursorId) => async (dispatch) => {
  try {
    const res = await candidateChatCall(
      'get',
      `candidate-chat/${jcId}/messages?limit=50&order_by=desc&cursor=${cursorId}&direction=before`
    );
    if (res?.status === 200) {
      const msgs = res.data.data?.messages || [];
      if (msgs.length > 0) {
        dispatch(appendChatMessages(msgs));
        return { success: true, hasMore: true };
      }
      return { success: true, hasMore: false };
    }
    return { success: false, hasMore: false };
  } catch (_) {
    return { success: false, hasMore: false };
  }
};

export const loadCandidateConversation = (jcId) => async (dispatch) => {
  try {
    const [convRes, msgRes] = await Promise.all([
      candidateChatCall('get', `candidate-chat/${jcId}/conversation`),
      candidateChatCall('get', `candidate-chat/${jcId}/messages?limit=50&order_by=desc`),
    ]);
    if (convRes?.status === 200) {
      dispatch(setChatConversation(convRes.data.data));
    }
    if (msgRes?.status === 200) {
      dispatch(setChatMessages(msgRes.data.data?.messages || []));
    }
    return { success: true };
  } catch (_) {
    return { success: false };
  }
};

export const searchCandidateMessages = (jcId, search) => async () => {
  try {
    const res = await candidateChatCall(
      'get',
      `candidate-chat/${jcId}/messages?limit=50&search=${encodeURIComponent(search)}`
    );
    if (res?.status === 200) {
      return { success: true, messages: res.data.data?.messages || [] };
    }
    return { success: false, messages: [] };
  } catch (_) {
    return { success: false, messages: [] };
  }
};

export const refreshCandidateConversation = (jcId) => async (dispatch) => {
  try {
    const res = await candidateChatCall('get', `candidate-chat/${jcId}/conversation`);
    if (res?.status === 200) dispatch(setChatConversation(res.data.data));
  } catch (_) {}
};

export const sendCandidateMessage = (jcId, bodyText, channel) => async (dispatch) => {
  try {
    const res = await candidateChatCall('post', `candidate-chat/${jcId}/messages`, {
      body_text: bodyText,
      channel,
    });
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertChatMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const markCandidateRead = (jcId, lastReadMessageId) => async () => {
  try {
    await candidateChatCall('patch', `candidate-chat/${jcId}/read`, {
      last_read_message_id: lastReadMessageId,
    });
    return { success: true };
  } catch (_) {
    return { success: false };
  }
};

// ─── Recruiter Comments (Notes) ────────────────────────────────────────────────

export const fetchCandidateComments = (jobId, jcId) => async () => {
  try {
    const res = await getJobApiCall(`jobs/${jobId}/job-candidates/${jcId}/comments`);
    if (res?.status === 200) return { success: true, data: res.data?.data || [] };
    return { success: false, data: [] };
  } catch (_) {
    return { success: false, data: [] };
  }
};

export const postCandidateComment = (jobId, jcId, bodyText, files = []) => async () => {
  try {
    const formData = new FormData();
    if (bodyText && bodyText.trim()) formData.append('body_text', bodyText.trim());
    files.forEach((f) => formData.append('files', f));
    const res = await axios.post(
      `${process.env.REACT_APP_AI_API_KEY}jobs/${jobId}/job-candidates/${jcId}/comments`,
      formData,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } },
    );
    if (res?.status === 200 || res?.status === 201) return { success: true, data: res.data?.data };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const patchCandidateComment = (jobId, jcId, commentId, bodyText) => async () => {
  try {
    const res = await patchJobApiCall(
      `jobs/${jobId}/job-candidates/${jcId}/comments/${commentId}`,
      { body_text: bodyText },
    );
    if (res?.status === 200) return { success: true, data: res.data?.data };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const deleteCandidateComment = (jobId, jcId, commentId) => async () => {
  try {
    const res = await deleteJobApiCall(`jobs/${jobId}/job-candidates/${jcId}/comments/${commentId}`);
    if (res?.status === 200 || res?.status === 204) return { success: true };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

// ─── Candidate Files ───────────────────────────────────────────────────────────

export const fetchCandidateFiles = (jobId, jcId) => async () => {
  try {
    const res = await getJobApiCall(`jobs/${jobId}/job-candidates/${jcId}/files`);
    if (res?.status === 200) return { success: true, data: res.data?.data || [] };
    return { success: false, data: [] };
  } catch (_) {
    return { success: false, data: [] };
  }
};

// ─── Candidate Activity ────────────────────────────────────────────────────────

export const fetchCandidateActivity = (jobId, jcId) => async () => {
  try {
    const res = await getJobApiCall(`jobs/${jobId}/job-candidates/${jcId}/activities`);
    if (res?.status === 200) return { success: true, data: res.data?.data || [] };
    return { success: false, data: [] };
  } catch (_) {
    return { success: false, data: [] };
  }
};

// ─── Team Chat plain action creators ──────────────────────────────────────────

export const setTeamInbox = (items) => ({ type: SET_TEAM_INBOX, payload: items });
export const upsertTeamInboxItem = (item) => ({ type: UPSERT_TEAM_INBOX_ITEM, payload: item });
export const setTeamConversation = (data) => ({ type: SET_TEAM_CONVERSATION, payload: data });
export const setTeamMessages = (messages) => ({ type: SET_TEAM_MESSAGES, payload: messages });
export const upsertTeamMessage = (message) => ({ type: UPSERT_TEAM_MESSAGE, payload: message });
export const appendTeamMessages = (messages) => ({ type: APPEND_TEAM_MESSAGES, payload: messages });
export const applyTeamReadReceipt = (userId, lastReadMessageId, lastReadAt) => ({
  type: APPLY_TEAM_READ_RECEIPT,
  payload: { user_id: userId, last_read_message_id: lastReadMessageId, last_read_at: lastReadAt },
});
export const setTeamTotalUnread = (count) => ({ type: SET_TEAM_TOTAL_UNREAD, payload: count });

const TEAM_API = 'internal-messages';

// ─── Team Chat thunks ─────────────────────────────────────────────────────────

export const loadTeamInbox = () => async (dispatch) => {
  try {
    const res = await getJobApiCall(`${TEAM_API}/conversations?page=1&page_size=50`);
    if (res?.status === 200) {
      dispatch(setTeamInbox(res.data.data?.items || []));
      dispatch(setTeamTotalUnread(res.data.data?.total_unread || 0));
    }
    return { success: true };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const refreshTeamMessages = (convId) => async (dispatch) => {
  try {
    const res = await getJobApiCall(`${TEAM_API}/conversations/${convId}/messages?limit=50`);
    if (res?.status === 200) {
      dispatch(setTeamMessages(res.data.data?.messages || []));
    }
  } catch (_) {}
};

export const loadTeamConversation = (convId) => async (dispatch) => {
  try {
    const [convRes, msgRes] = await Promise.all([
      getJobApiCall(`${TEAM_API}/conversations/${convId}`),
      getJobApiCall(`${TEAM_API}/conversations/${convId}/messages?limit=50`),
    ]);
    if (convRes?.status === 200) dispatch(setTeamConversation(convRes.data.data));
    const msgs = msgRes?.status === 200 ? (msgRes.data.data?.messages || []) : [];
    dispatch(setTeamMessages(msgs));
    return { success: true, conversation: convRes?.data?.data, messages: msgs };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const loadOlderTeamMessages = (convId, cursorId) => async (dispatch) => {
  try {
    const res = await getJobApiCall(
      `${TEAM_API}/conversations/${convId}/messages?limit=50&cursor=${cursorId}&direction=before`
    );
    if (res?.status === 200) {
      const msgs = res.data.data?.messages || [];
      if (msgs.length > 0) {
        dispatch(appendTeamMessages(msgs));
        return { success: true, hasMore: true };
      }
      return { success: true, hasMore: false };
    }
    return { success: false, hasMore: false };
  } catch (_) {
    return { success: false, hasMore: false };
  }
};

export const sendTeamMessage = (convId, bodyText) => async (dispatch) => {
  try {
    const res = await postJobApiCall(`${TEAM_API}/conversations/${convId}/messages`, { body_text: bodyText });
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertTeamMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const sendTeamMessageWithAttachment = (convId, file, caption) => async (dispatch) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    if (caption) formData.append('body_text', caption);
    const res = await axios.post(
      `${process.env.REACT_APP_AI_API_KEY}${TEAM_API}/conversations/${convId}/messages/with-attachment`,
      formData,
      { headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` } }
    );
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(upsertTeamMessage(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const markTeamRead = (convId, lastReadMessageId) => async () => {
  try {
    await patchJobApiCall(`${TEAM_API}/conversations/${convId}/read`, { last_read_message_id: lastReadMessageId });
    return { success: true };
  } catch (_) {
    return { success: false };
  }
};

export const createTeamDirect = (otherUserId) => async (dispatch) => {
  try {
    const res = await postJobApiCall(`${TEAM_API}/direct`, { other_user_id: otherUserId });
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(setTeamConversation(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const createTeamGroup = (title, memberUserIds) => async (dispatch) => {
  try {
    const res = await postJobApiCall(`${TEAM_API}/groups`, { title, member_user_ids: memberUserIds });
    if (res?.status === 200 || res?.status === 201) {
      if (res.data?.data) dispatch(setTeamConversation(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (error) {
    handle403Logout(error);
    return { success: false };
  }
};

export const loadTeamGroupMembers = (convId) => async () => {
  try {
    const res = await getJobApiCall(`${TEAM_API}/groups/${convId}/members`);
    if (res?.status === 200) return { success: true, data: res.data.data?.items || [] };
    return { success: false, data: [] };
  } catch (_) {
    return { success: false, data: [] };
  }
};

export const addTeamGroupMembers = (convId, memberUserIds) => async () => {
  try {
    const res = await postJobApiCall(`${TEAM_API}/groups/${convId}/members`, { member_user_ids: memberUserIds });
    if (res?.status === 200 || res?.status === 201) return { success: true };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const renameTeamGroup = (convId, title) => async (dispatch) => {
  try {
    const res = await patchJobApiCall(`${TEAM_API}/groups/${convId}`, { title });
    if (res?.status === 200) {
      if (res.data?.data) dispatch(setTeamConversation(res.data.data));
      return { success: true, data: res.data?.data };
    }
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const leaveTeamGroup = (convId) => async () => {
  try {
    const res = await deleteJobApiCall(`${TEAM_API}/groups/${convId}/members/me`);
    if (res?.status === 200 || res?.status === 204) return { success: true };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const removeTeamGroupMember = (convId, userId) => async () => {
  try {
    const res = await deleteJobApiCall(`${TEAM_API}/groups/${convId}/members/${userId}`);
    if (res?.status === 200 || res?.status === 204) return { success: true };
    return { success: false };
  } catch (_) {
    return { success: false };
  }
};

export const loadCompanyMembers = () => async () => {
  try {
    const res = await getJobApiCall('company-members?page=1&page_size=200&is_active=true');
    if (res?.status === 200) {
      const rows = Array.isArray(res.data?.data) ? res.data.data : [];
      return { success: true, data: rows };
    }
    return { success: false, data: [] };
  } catch (_) {
    return { success: false, data: [] };
  }
};
